'use client';

import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Main from '@/components/main';
import MaterialLink from '@/components/material-link';
import { DISCORD_INVITE_URL } from '@/constants/DISCORD_INVITE_URL';

export default function DiscordPageClient({ discordOAuthUrl }: { discordOAuthUrl: string }) {
  return (
    <Main compact>
      <Typography variant="h1">Everyday Rides on Discord</Typography>

      <p>
        Get ride notifications in Discord. Events post to connected Discord channels the moment they&apos;re published, with Going, Interested, and Not Going buttons so you can RSVP right from the message. Posts update automatically if an event changes or is cancelled, and event comments are forwarded to threads so the conversation stays in sync.
      </p>

      <p>
        The official Everyday Rides Discord carries a live feed of Seattle events, plus announcements and help with the site. Seattle events also post to the <MaterialLink href="https://tinyurl.com/seattlecyclist">Seattle Cyclist</MaterialLink> server, if that&apos;s where you already hang out.
      </p>

      <Box sx={{ textAlign: 'center', py: 2 }}>
        <Button
          component="a"
          href={DISCORD_INVITE_URL}
          target="_blank"
          rel="noopener noreferrer"
          variant="contained"
          size="large"
          sx={{
            bgcolor: '#5865F2',
            '&:hover': { bgcolor: '#4752C4' },
          }}
        >
          Join the Everyday Rides Discord
        </Button>
      </Box>

      <Typography variant="h2">Get DM reminders</Typography>

      <p>Link your Discord account to Everyday Rides to get direct messages for rides you&apos;ve RSVP&apos;d to — reminders before events, updates when events change, and cancellation notices.</p>

      <ol>
        <li>Run <code>/er-link</code> in any channel where the bot is installed</li>
        <li>Click the link to confirm on the Everyday Rides website</li>
      </ol>

      <p>Once linked, you can use the buttons on any event post to RSVP. You can unlink anytime with <code>/er-unlink</code> or from your account settings on the website. Use <code>/er-settings</code> to manage your DM notification preferences.</p>

      <p>You can also run <code>/er-upcoming</code> in any connected channel to see a list of upcoming events. The response is only visible to you.</p>

      <Table size="small" sx={{ my: 2 }}>
        <TableHead>
          <TableRow>
            <TableCell>Command</TableCell>
            <TableCell>Description</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell><code>/er-link</code></TableCell>
            <TableCell>Link your Discord account to Everyday Rides</TableCell>
          </TableRow>
          <TableRow>
            <TableCell><code>/er-unlink</code></TableCell>
            <TableCell>Unlink your Discord account</TableCell>
          </TableRow>
          <TableRow>
            <TableCell><code>/er-upcoming</code></TableCell>
            <TableCell>See upcoming events (only visible to you)</TableCell>
          </TableRow>
          <TableRow>
            <TableCell><code>/er-settings</code></TableCell>
            <TableCell>Manage your DM notification preferences</TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <Typography variant="h2" id="group-owners">For group owners</Typography>

      <p>If you run a group on Everyday Rides and want events posted to your Discord server, you can connect a channel to your group. You&apos;ll need Manage Server permission in Discord to add the bot.</p>

      <ol>
        <li>Add the Everyday Rides bot to your Discord server</li>
        <li>Link your account with <code>/er-link</code></li>
        <li>Run <code>/er-connect group</code> and choose which group&apos;s events to post to the channel</li>
      </ol>

      <Box sx={{ textAlign: 'center', py: 2 }}>
        <Button
          component="a"
          href={discordOAuthUrl}
          target="_blank"
          rel="noopener noreferrer"
          variant="contained"
          size="large"
          sx={{
            bgcolor: '#5865F2',
            '&:hover': { bgcolor: '#4752C4' },
          }}
        >
          Add to Discord
        </Button>
      </Box>

      <p>Once connected, new events will be posted to the channel automatically. You can use <code>/er-disconnect</code> to stop posting to a channel.</p>

      <Table size="small" sx={{ my: 2 }}>
        <TableHead>
          <TableRow>
            <TableCell>Command</TableCell>
            <TableCell>Description</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell><code>/er-connect group</code></TableCell>
            <TableCell>Connect a group&apos;s events to a channel</TableCell>
          </TableRow>
          <TableRow>
            <TableCell><code>/er-disconnect</code></TableCell>
            <TableCell>Stop posting events to a channel</TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <Typography variant="h2">For city admins</Typography>

      <p>Site admins can connect a Discord channel to a city&apos;s events using <code>/er-connect city</code> instead of <code>/er-connect group</code>. The setup is otherwise the same as above.</p>

      <Table size="small" sx={{ my: 2 }}>
        <TableHead>
          <TableRow>
            <TableCell>Command</TableCell>
            <TableCell>Description</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell><code>/er-connect city</code></TableCell>
            <TableCell>Connect a city&apos;s public events to a channel</TableCell>
          </TableRow>
          <TableRow>
            <TableCell><code>/er-disconnect</code></TableCell>
            <TableCell>Stop posting events to a channel</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Main>
  );
}
